import React from 'react';
import { formatCurrency, Currency } from '../utils/currencyUtils';

interface JsonFormatterProps {
  data: any;
  title?: string;
}

const JsonFormatter: React.FC<JsonFormatterProps> = ({ data, title }) => {
  // Helper to format dates
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  // Helper to format currency values
  const formatValue = (value: string | number | null, currency?: string): string => {
    if (value === null || value === undefined) return 'N/A';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return String(value);
    const currencyCode = (currency || 'INR') as Currency;
    return formatCurrency(numValue, currencyCode);
  };

  // Render a single record as a card
  const renderRecordCard = (record: any, index: number) => {
    return (
      <div
        key={index}
        className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-6 mb-4"
      >
        <div className="flex items-start justify-between mb-4">
          <h4 className="text-lg font-bold text-gray-900">{record.name || `Record ${index + 1}`}</h4>
          {record.stage && (
            <span className="px-3 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800 capitalize">
              {record.stage.replace('_', ' ')}
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {record.value !== undefined && (
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Value</span>
              <p className="text-lg font-bold text-gray-900 mt-1">
                {formatValue(record.value, record.currency)}
              </p>
            </div>
          )}

          {record.probability !== undefined && record.probability !== null && (
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Probability</span>
              <div className="mt-1">
                <div className="flex items-center space-x-2">
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-indigo-500 to-purple-500 h-2 rounded-full"
                      style={{ width: `${record.probability}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-semibold text-gray-700">{record.probability}%</span>
                </div>
              </div>
            </div>
          )}

          {record.expected_close_date && (
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Expected Close Date</span>
              <p className="text-sm font-medium text-gray-700 mt-1">{formatDate(record.expected_close_date)}</p>
            </div>
          )}

          {record.owner_id && (
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Owner ID</span>
              <p className="text-sm font-medium text-gray-700 mt-1">#{record.owner_id}</p>
            </div>
          )}

          {record.account_id && (
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Account ID</span>
              <p className="text-sm font-medium text-gray-700 mt-1">#{record.account_id}</p>
            </div>
          )}

          {record.created_at && (
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Created</span>
              <p className="text-sm font-medium text-gray-700 mt-1">{formatDate(record.created_at)}</p>
            </div>
          )}

          {record.currency && (
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Currency</span>
              <p className="text-sm font-medium text-gray-700 mt-1">{record.currency}</p>
            </div>
          )}
        </div>

        {record.description && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Description</span>
            <p className="text-sm text-gray-700 mt-1">{record.description}</p>
          </div>
        )}
      </div>
    );
  };

  // Handle array of records
  if (Array.isArray(data)) {
    if (data.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <p>No records found</p>
        </div>
      );
    }

    return (
      <div>
        {title && (
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            {title} ({data.length} {data.length === 1 ? 'record' : 'records'})
          </h3>
        )}
        <div className="space-y-4">
          {data.map((record, index) => renderRecordCard(record, index))}
        </div>
      </div>
    );
  }

  // Handle object with data array
  if (data && typeof data === 'object' && data.data && Array.isArray(data.data)) {
    return (
      <div>
        {data.message && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm font-medium text-blue-800">{data.message}</p>
          </div>
        )}
        {data.count !== undefined && (
          <div className="mb-4">
            <span className="text-sm font-semibold text-gray-600">
              Total: {data.count} {data.count === 1 ? 'record' : 'records'}
            </span>
          </div>
        )}
        <div className="space-y-4">
          {data.data.map((record: any, index: number) => renderRecordCard(record, index))}
        </div>
      </div>
    );
  }

  // Handle plain object
  if (data && typeof data === 'object') {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="space-y-3">
          {Object.entries(data).map(([key, value]) => (
            <div key={key} className="flex items-start">
              <span className="text-sm font-semibold text-gray-600 w-32 flex-shrink-0 capitalize">
                {key.replace(/_/g, ' ')}:
              </span>
              <span className="text-sm text-gray-900 flex-1">
                {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Fallback for other types
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <pre className="text-sm text-gray-700 whitespace-pre-wrap overflow-x-auto">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
};

export default JsonFormatter;

